/* eslint-disable no-restricted-globals */
import {Dispatch, SetStateAction, useCallback, useEffect, useState} from "react";
import {Static, StaticDecode, TSchema} from "typebox";
import {Value} from "typebox/value";

// List of callbacks to call when local storage is changed to allow
// components to synchronize with each other
const LOCAL_STORAGE_WATCHERS = new Map<string, Set<() => void>>();

// Listen to storage changes from other windows
addEventListener("storage", event => {
  if (event.storageArea === localStorage) {
    const watchers =
      event.key == null
        ? [...LOCAL_STORAGE_WATCHERS.values()].flatMap(cbs => [...cbs])
        : [...(LOCAL_STORAGE_WATCHERS.get(event.key) ?? [])];
    for (const watcher of watchers) {
      watcher();
    }
  }
});

type TJson<T extends TSchema> = Static<T> extends unknown ? T : never;

// Helper function to deserialize a state from local storage, and
// return a default value if the state is unset or otherwise fails
// to deserialize.
export function readLocalState<S extends TSchema>(
  storageKey: string,
  schema: TJson<S>,
  defaultValue: StaticDecode<S>,
): StaticDecode<S> {
  const storedValue = localStorage.getItem(storageKey);
  try {
    if (storedValue) {
      return Value.Decode(schema, JSON.parse(storedValue));
    } else {
      return defaultValue;
    }
  } catch (_ex) {
    return defaultValue;
  }
}

// Helper function to serialize a state to session storage
export function writeLocalState<S extends TSchema>(storageKey: string, schema: TJson<S>, value: StaticDecode<S>) {
  // Save the new state
  localStorage.setItem(storageKey, JSON.stringify(Value.Encode(schema, value)));
  // Run any watchers to ensure all components receive the new state
  const watchers = LOCAL_STORAGE_WATCHERS.get(storageKey);
  if (watchers) {
    for (const watcher of watchers) {
      watcher();
    }
  }
}

// Similar to `useState`, except that state is persisted in `localStorage` under
// the provided `storageKey`. State must be JSON serializable.
export function useLocalState<S extends TSchema>(
  storageKey: string,
  schema: TJson<S>,
  defaultValue: StaticDecode<S>,
): [StaticDecode<S>, Dispatch<SetStateAction<StaticDecode<S>>>] {
  // On state initialization, attempt to read the value from `localStorage`.
  const [state, setState] = useState<StaticDecode<S>>(() => readLocalState(storageKey, schema, defaultValue));

  // Register a local storage watcher for this storage key so we get notified
  // if another component modifies the `localStorage`.
  useEffect(() => {
    // Define a callback which updates our state based on the local storage
    const cb = () => {
      setState(readLocalState(storageKey, schema, defaultValue));
    };
    // When the effect runs, we add the watcher
    if (!LOCAL_STORAGE_WATCHERS.has(storageKey)) {
      LOCAL_STORAGE_WATCHERS.set(storageKey, new Set());
    }
    LOCAL_STORAGE_WATCHERS.get(storageKey)!.add(cb);

    // In the effect destructor, we remove the watcher
    return () => {
      LOCAL_STORAGE_WATCHERS.get(storageKey)!.delete(cb);
    };
  }, [storageKey, schema, defaultValue]);

  // This is the function we return to the caller to allow them to set the state
  const wrappedSetState = useCallback(
    // Mirroring `useState`, the argument can be either a state value or a state-updating function
    (newValueOrFn: SetStateAction<StaticDecode<S>>) => {
      // If the caller provided a function, then apply the function to the freshly-read state
      let newValue: StaticDecode<S>;
      if (typeof newValueOrFn === "function") {
        newValue = (newValueOrFn as (oldValue: StaticDecode<S>) => StaticDecode<S>)(
          readLocalState(storageKey, schema, defaultValue),
        );
      } else {
        newValue = newValueOrFn;
      }
      writeLocalState(storageKey, schema, newValue);
    },
    [storageKey, schema, defaultValue],
  );

  // Finally, return the state and state setter
  return [state, wrappedSetState];
}

export function clearLocalState(matcher?: RegExp) {
  if (matcher) {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && matcher.test(key)) {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) {
      localStorage.removeItem(key);
      const watchers = LOCAL_STORAGE_WATCHERS.get(key);
      if (watchers) {
        for (const watcher of watchers) {
          watcher();
        }
      }
    }
  } else {
    localStorage.clear();
    for (const watchers of LOCAL_STORAGE_WATCHERS.values()) {
      for (const watcher of watchers) {
        watcher();
      }
    }
  }
}
